import optionsApi from "./optionsApi";
import { downloadBlobObject } from "../utils/DownloadFile";

export interface SystemVariable {
  variable: string;
  label: string;
  description: string;
  model: string;
}

export interface ExtractVariablesResult {
  systemVars: string[];
  customVars: string[];
  suspiciousVars: string[];
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface CampaignRow {
  employeeId: string;
  employeeHrId: string;
  customVars: Record<string, string>;
}

export interface SavedCampaignTemplate {
  id: string;
  templateName: string;
  subject: string;
  s3Path: string;
  updatedAt: string;
}

export async function convertDocxToHtml(base64Docx: string): Promise<string> {
  const res = await optionsApi.post("v1/template/docx-to-html", {
    file: base64Docx,
  });
  return res.data.data.html as string;
}

export async function getSystemVariables(): Promise<SystemVariable[]> {
  const res = await optionsApi.get("v1/template/system-variables");
  return res.data.data as SystemVariable[];
}

export async function extractVariables(
  html: string
): Promise<ExtractVariablesResult> {
  const res = await optionsApi.post("v1/template/extract-variables", { html });
  return res.data.data as ExtractVariablesResult;
}

export async function downloadCustomEmailExcel(
  customVars: string[]
): Promise<void> {
  const res = await optionsApi.post(
    "v1/template/custom-email-excel",
    { customVars },
    { responseType: "arraybuffer" }
  );
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlobObject(blob, "custom_email_recipients.xlsx");
}

export async function validateCustomEmailExcel(
  file: File,
  customVars: string[]
): Promise<ValidationResult> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("customVars", JSON.stringify(customVars));
  const res = await optionsApi.post(
    "v1/template/validate-custom-email-excel",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return res.data.data as ValidationResult;
}

export interface ParsedExcelRow {
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  employeeHrId: string;
  customVars: Record<string, string>;
}

export async function parseCustomEmailExcel(
  file: File,
  customVars: string[]
): Promise<ParsedExcelRow[]> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("customVars", JSON.stringify(customVars));
  const res = await optionsApi.post(
    "v1/template/parse-custom-email-excel",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return res.data.data as ParsedExcelRow[];
}

export interface CampaignSendResult {
  sent: number;
  failed: number;
  results: {
    hrId: string;
    name: string;
    email: string;
    success: boolean;
    error?: string;
  }[];
}

export async function sendCustomEmailCampaign(
  htmlTemplate: string,
  subject: string,
  rows: CampaignRow[]
): Promise<CampaignSendResult> {
  const res = await optionsApi.post("v1/template/send-custom-email-campaign", {
    htmlTemplate,
    subject,
    rows,
  });
  return res.data.data as CampaignSendResult;
}

export async function saveCustomCampaignTemplate(
  templateName: string,
  htmlContent: string,
  subject: string
): Promise<SavedCampaignTemplate> {
  const res = await optionsApi.post("v1/template/custom-campaigns", {
    templateName,
    htmlContent,
    subject,
  });
  return res.data.data as SavedCampaignTemplate;
}

export async function getSavedCampaignTemplates(): Promise<
  SavedCampaignTemplate[]
> {
  const res = await optionsApi.get("v1/template/custom-campaigns");
  return res.data.data as SavedCampaignTemplate[];
}

export async function sendTestEmailCampaign(
  htmlTemplate: string,
  subject: string,
  employeeHrId: string,
  adminEmail: string,
  customVarSamples?: Record<string, string>
): Promise<void> {
  await optionsApi.post("v1/template/send-test-email", {
    htmlTemplate,
    subject,
    employeeHrId,
    adminEmail,
    customVarSamples: customVarSamples ?? {},
  });
}

export async function getCampaignWrapper(): Promise<string> {
  const res = await optionsApi.get("v1/template/campaign-wrapper");
  return res.data.data.html as string;
}

export async function generateEmailBodyFromPrompt(
  prompt: string
): Promise<string> {
  const res = await optionsApi.post("v1/template/ai/generate", { prompt });
  return res.data.data.html as string;
}

export async function polishEmailMessage(html: string): Promise<string> {
  const res = await optionsApi.post("v1/template/ai/polish-message", { html });
  return res.data.data.html as string;
}

export async function fixEmailStyleApi(
  html: string,
  stylePrompt?: string
): Promise<string> {
  const res = await optionsApi.post("v1/template/ai/fix-style", {
    html,
    stylePrompt,
  });
  return res.data.data.html as string;
}
